import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';
import {
  formatTradeForManualApi,
  manualTradePayloadSignature,
  tradeRowPassesValidation,
} from '../utils/manualTradeApi';
import BrokerSyncPanel from './BrokerSyncPanel';
import './ManualTradeEntry.css';

/** The OAuth flash renders before /api/brokers/status resolves, so derive a label from the id. */
function brokerLabelFromId(id) {
  if (!id) return 'Broker';
  return id.charAt(0).toUpperCase() + id.slice(1);
}

function readBrokerUrlFlash() {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search);
  const broker = p.get('broker');
  if (p.get('brokerStatus') === 'connected') return { kind: 'connected', broker };
  const err = p.get('brokerSync');
  if (err) return { kind: 'error', code: err, broker };
  return null;
}

function stripBrokerQueryParams() {
  const u = new URL(window.location.href);
  ['broker', 'brokerStatus', 'brokerSync', 'tab', 'brokerTab'].forEach((k) =>
    u.searchParams.delete(k)
  );
  const next = u.pathname + (u.search || '');
  window.history.replaceState({}, '', next || '/');
}

function normalizeNotesForCompare(form) {
  const selections = {};
  for (const [k, v] of Object.entries(form.selections || {})) {
    const s = String((v && (v.label ?? v.value ?? v.code)) || '').trim();
    if (s) selections[k] = s;
  }
  const keys = Object.keys(selections).sort();
  const sorted = {};
  for (const k of keys) sorted[k] = selections[k];
  return JSON.stringify({
    freeText: String(form.freeText || '').trim(),
    selections: sorted
  });
}

function ManualTradeEntry({ onSuccess }) {
  const [tradeBooks, setTradeBooks] = useState([]);
  const [selectedTradeBookId, setSelectedTradeBookId] = useState('');
  const [isNewTradeBook, setIsNewTradeBook] = useState(false);
  const [newTradeBookName, setNewTradeBookName] = useState(`TradeBook_${Date.now()}`);
  const [nameError, setNameError] = useState('');
  const emptyRow = () => ({
    id: null,
    symbol: '',
    tradeType: 'BUY',
    quantity: '',
    price: '',
    exitPrice: '',
    tradeDate: '',
    segment: 'EQ',
    expiry: '',
    broker: '',
    brokerTradeId: '',
    brokerOrderId: '',
  });
  const [trades, setTrades] = useState(Array(20).fill(null).map(() => emptyRow()));
  const [loading, setLoading] = useState(false);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [importingBrokerId, setImportingBrokerId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  /** Import result under Import button — cleared when session-expired message shows */
  const [brokerImportMessage, setBrokerImportMessage] = useState('');
  const [tradeBookCount, setTradeBookCount] = useState({ count: 0, maxCount: 5 });
  const [brokerStatus, setBrokerStatus] = useState(null);
  const [noteMeta, setNoteMeta] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesModalTrade, setNotesModalTrade] = useState(null);
  const [notesForm, setNotesForm] = useState({ freeText: '', selections: {} });
  const [notesFetching, setNotesFetching] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaveError, setNotesSaveError] = useState('');
  const manualTradeBaselineRef = useRef({});
  const notesLoadedCompareRef = useRef('');

  const brokers = brokerStatus?.brokers || [];

  useEffect(() => {
    const flash = readBrokerUrlFlash();
    if (flash?.kind === 'connected') {
      setBrokerImportMessage(
        `${brokerLabelFromId(flash.broker)} connected. Import today’s fills, review the table, then click Submit Trades.`
      );
      setError('');
    } else if (flash?.kind === 'error') {
      const label = brokerLabelFromId(flash.broker);
      setError(
        `${label} login did not complete (${flash.code.replace(/_/g, ' ')}). Try Connect ${label} again.`
      );
      setBrokerImportMessage('');
    }
    if (flash) stripBrokerQueryParams();
  }, []);

  useEffect(() => {
    fetchTradeBooks();
    fetchTradeBookCount();
    fetchNoteMeta();
    fetchBrokerStatus();
  }, []);

  useEffect(() => {
    if (!isNewTradeBook && selectedTradeBookId) {
      fetchTradesForBook(selectedTradeBookId);
    } else if (isNewTradeBook) {
      manualTradeBaselineRef.current = {};
      setTrades(Array(20).fill(null).map(() => emptyRow()));
    }
  }, [isNewTradeBook, selectedTradeBookId]);

  const fetchTradeBooks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trade-books`);
      setTradeBooks(response.data);
      if (response.data.length > 0) {
        setSelectedTradeBookId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching trade books:', error);
    }
  };

  const fetchTradeBookCount = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trade-books/count`);
      setTradeBookCount(response.data);
    } catch (error) {
      console.error('Error fetching trade book count:', error);
    }
  };

  const fetchNoteMeta = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/trade-notes/meta`);
      setNoteMeta(res.data.noteTypes || []);
    } catch (error) {
      console.error('Error fetching trade notes meta:', error);
    }
  };

  const fetchBrokerStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/brokers/status`);
      setBrokerStatus(res.data);
      if ((res.data?.brokers || []).some((b) => b.sessionExpired)) {
        setBrokerImportMessage('');
      }
    } catch (e) {
      console.error('Broker status:', e);
      setBrokerStatus(null);
    }
  };

  const fetchTradesForBook = async (tradeBookId) => {
    if (!tradeBookId) return;
    setLoadingTrades(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trades?tradeBookId=${tradeBookId}`);
      const list = response.data.trades || [];
      const rows = list.map((t) => ({
        id: t.id,
        symbol: t.symbol?.replace(/_\d{4}-\d{2}-\d{2}$/, '') || '',
        tradeType: t.tradeType || 'BUY',
        quantity: t.quantity ?? '',
        price: t.price ?? '',
        exitPrice: t.exitPrice != null ? t.exitPrice : '',
        tradeDate: t.tradeDate || '',
        segment: t.segment || 'EQ',
        expiry: t.expiry || '',
        broker: t.broker || '',
        brokerTradeId: t.brokerTradeId || '',
        brokerOrderId: t.brokerOrderId || '',
      }));
      const baseline = {};
      for (const t of rows) {
        if (t.id) baseline[t.id] = manualTradePayloadSignature(t);
      }
      manualTradeBaselineRef.current = baseline;

      const emptyRows = Array(10).fill(null).map(() => emptyRow());
      setTrades([...rows, ...emptyRows]);
    } catch (err) {
      console.error('Error fetching trades for book:', err);
      manualTradeBaselineRef.current = {};
      setTrades(Array(20).fill(null).map(() => emptyRow()));
    } finally {
      setLoadingTrades(false);
    }
  };

  const brokerLabel = (brokerId) =>
    brokers.find((b) => b.id === brokerId)?.label || brokerLabelFromId(brokerId);

  const connectBroker = async (brokerId) => {
    setError('');
    setMessage('');
    setBrokerImportMessage('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/brokers/${brokerId}/connect-start`);
      window.location.href = res.data.loginUrl;
    } catch (e) {
      setError(e.response?.data?.error || `Could not start ${brokerLabel(brokerId)} login`);
    }
  };

  const importTodaysFills = async (brokerId) => {
    setError('');
    setMessage('');
    setBrokerImportMessage('');

    if (!isNewTradeBook && !selectedTradeBookId) {
      setError('Please select a trade book or create a new one before importing');
      return;
    }
    if (isNewTradeBook) {
      if (!newTradeBookName || newTradeBookName.trim() === '') {
        setNameError('Trade book name is required');
        return;
      }
      if (!/^[a-zA-Z0-9_]+$/.test(newTradeBookName)) {
        setNameError('Trade book name can only contain letters, numbers, and underscores');
        return;
      }
    }

    setImportingBrokerId(brokerId);
    try {
      const payload = {};
      if (!isNewTradeBook && selectedTradeBookId) {
        payload.tradeBookId = selectedTradeBookId;
      }
      const res = await axios.post(
        `${API_BASE_URL}/api/brokers/${brokerId}/import-todays-fills`,
        payload
      );
      const incoming = res.data.trades || [];
      const skipped = res.data.skippedCount || 0;

      const pendingKeys = new Set(
        trades
          .filter((t) => t && !t.id && t.broker === brokerId && t.brokerTradeId)
          .map((t) => `${t.brokerTradeId}|${t.tradeDate}`)
      );

      const toAppend = [];
      for (const row of incoming) {
        const key = `${row.brokerTradeId}|${row.tradeDate}`;
        if (pendingKeys.has(key)) continue;
        pendingKeys.add(key);
        toAppend.push({
          id: null,
          symbol: row.symbol || '',
          tradeType: row.tradeType,
          quantity: row.quantity,
          price: row.price,
          exitPrice: '',
          tradeDate: row.tradeDate,
          segment: row.segment || 'EQ',
          expiry: row.expiry || '',
          broker: row.broker || brokerId,
          brokerTradeId: row.brokerTradeId || '',
          brokerOrderId: row.brokerOrderId || '',
        });
      }

      if (toAppend.length === 0) {
        let msg = 'No new fills to add.';
        if (skipped > 0) {
          msg += ` ${skipped} fill(s) skipped (already in this trade book or duplicate in this import).`;
        }
        setBrokerImportMessage(msg);
        await fetchBrokerStatus();
        return;
      }

      setTrades((prev) => {
        let lastFilled = -1;
        for (let i = 0; i < prev.length; i++) {
          const t = prev[i];
          if (t && (t.id || tradeRowPassesValidation(t))) lastFilled = i;
        }
        const head = prev.slice(0, lastFilled + 1);
        const rest = prev.slice(lastFilled + 1);
        let merged = [...head, ...toAppend, ...rest];
        if (merged.length < 20) {
          merged = [...merged, ...Array(20 - merged.length).fill(null).map(() => emptyRow())];
        }
        return merged;
      });

      let msg = `Imported ${toAppend.length} fill(s) into the table. Review the rows, then click Submit Trades to save them.`;
      if (skipped > 0) {
        msg += ` ${skipped} fill(s) were skipped (already in this trade book or duplicate).`;
      }
      setBrokerImportMessage(msg);
      await fetchBrokerStatus();
      if (onSuccess) onSuccess();
    } catch (e) {
      const code = e.response?.data?.code || '';
      if (e.response?.status === 401 && /SESSION_EXPIRED|NOT_CONNECTED/.test(code)) {
        setBrokerImportMessage('');
        await fetchBrokerStatus();
      } else {
        setError(e.response?.data?.error || 'Import failed');
      }
    } finally {
      setImportingBrokerId(null);
    }
  };

  const handleTradeChange = (index, field, value) => {
    const updatedTrades = [...trades];
    updatedTrades[index] = {
      ...updatedTrades[index],
      [field]: value
    };
    setTrades(updatedTrades);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (/^[a-zA-Z0-9_]*$/.test(value) && value.length <= 100) {
      setNewTradeBookName(value);
      setNameError('');
    } else if (value.length > 100) {
      setNameError('Trade book name must be 100 characters or less');
    } else {
      setNameError('Trade book name can only contain letters, numbers, and underscores');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setBrokerImportMessage('');

    // Validate trade book selection
    if (!isNewTradeBook && !selectedTradeBookId) {
      setError('Please select a trade book or create a new one');
      return;
    }

    if (isNewTradeBook) {
      if (!newTradeBookName || newTradeBookName.trim() === '') {
        setNameError('Trade book name is required');
        return;
      }
      if (!/^[a-zA-Z0-9_]+$/.test(newTradeBookName)) {
        setNameError('Trade book name can only contain letters, numbers, and underscores');
        return;
      }
    }

    // Reject modified saved rows that are no longer valid
    for (let i = 0; i < trades.length; i++) {
      const trade = trades[i];
      if (!trade?.id) continue;
      const baseline = manualTradeBaselineRef.current[trade.id];
      if (baseline == null) continue;
      const dirty = manualTradePayloadSignature(trade) !== baseline;
      if (dirty && !tradeRowPassesValidation(trade)) {
        setError(
          `Row ${i + 1}: Fix required fields on edited trades (symbol, quantity, price, trade date, and exit date if exit price is set).`
        );
        return;
      }
    }

    const syncEntries = [];
    for (let i = 0; i < trades.length; i++) {
      const trade = trades[i];
      if (!tradeRowPassesValidation(trade)) continue;
      const rowNum = i + 1;
      if (!trade.id) {
        syncEntries.push({ rowNum, payload: formatTradeForManualApi(trade) });
        continue;
      }
      const baseline = manualTradeBaselineRef.current[trade.id];
      if (baseline == null || manualTradePayloadSignature(trade) !== baseline) {
        syncEntries.push({ rowNum, payload: formatTradeForManualApi(trade) });
      }
    }

    if (syncEntries.length === 0) {
      setMessage('No trade changes to save.');
      return;
    }

    for (const { rowNum, payload: trade } of syncEntries) {
      if (!trade.symbol.trim()) {
        setError(`Row ${rowNum}: Symbol is required`);
        return;
      }
      if (!trade.quantity || parseFloat(trade.quantity) <= 0) {
        setError(`Row ${rowNum}: Valid quantity is required`);
        return;
      }
      if (!trade.price || parseFloat(trade.price) <= 0) {
        setError(`Row ${rowNum}: Valid entry price is required`);
        return;
      }
      if (trade.exitPrice != null && (parseFloat(trade.exitPrice) < 0 || isNaN(parseFloat(trade.exitPrice)))) {
        setError(`Row ${rowNum}: Exit price must be a non-negative number if provided`);
        return;
      }
      if (trade.exitPrice != null) {
        if (!trade.exitDate) {
          setError(`Row ${rowNum}: Exit/Expiry date is required when exit price is provided (use as exit date)`);
          return;
        }
      }
      if (!trade.tradeDate) {
        setError(`Row ${rowNum}: Trade date is required`);
        return;
      }
    }

    const tradesToSync = syncEntries.map((e) => e.payload);

    setLoading(true);

    try {
      let tradeBookId = selectedTradeBookId;

      // Create new trade book if needed
      if (isNewTradeBook) {
        const createResponse = await axios.post(`${API_BASE_URL}/api/trade-books`, {
          name: newTradeBookName.trim()
        });
        tradeBookId = createResponse.data.id;
      }

      const res = await axios.post(`${API_BASE_URL}/api/trades/manual`, {
        tradeBookId,
        trades: tradesToSync
      });

      setMessage(res.data.message || `Successfully saved trade book.`);
      
      // Reload trades if existing book, else reset to empty rows
      if (!isNewTradeBook && tradeBookId) {
        await fetchTradesForBook(tradeBookId);
      } else {
        setTrades(Array(20).fill(null).map(() => emptyRow()));
      }

      // Refresh trade books and count
      await fetchTradeBooks();
      await fetchTradeBookCount();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add trades');
    } finally {
      setLoading(false);
    }
  };

  const addMoreRows = () => {
    setTrades([...trades, ...Array(10).fill(null).map(() => emptyRow())]);
  };

  const openNotesModal = async (trade) => {
    if (!trade?.id) return;
    setNotesModalTrade(trade);
    setNotesSaveError('');
    setNotesForm({ freeText: '', selections: {} });
    setNotesFetching(true);
    setNotesModalOpen(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/trade-notes/${trade.id}`);
      const data = res.data || {};
      const formState = {
        freeText: data.freeText || '',
        selections: data.selections || {}
      };
      setNotesForm(formState);
      notesLoadedCompareRef.current = normalizeNotesForCompare(formState);
    } catch (e) {
      console.error('Error loading trade notes:', e);
      setNotesSaveError('Could not load notes. Try again or close and reopen.');
    } finally {
      setNotesFetching(false);
    }
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setNotesModalTrade(null);
    setNotesSaveError('');
  };

  useEffect(() => {
    if (!notesModalOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape' || notesFetching || notesSaving) return;
      closeNotesModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [notesModalOpen, notesFetching, notesSaving]);

  return (
    <div className="manual-trade-entry">
      <div className="card">
        <h2>Trade Wizard — Manual Entry &amp; Broker Sync</h2>
        <p className="entry-description">
          Enter trades manually in the table, or connect a broker and import today&apos;s fills. Review rows, then click
          Submit Trades.
        </p>

        {tradeBookCount.count >= tradeBookCount.maxCount && !isNewTradeBook && (
          <div className="warning-message">
            <strong>Maximum Trade Books Reached:</strong> You have reached the maximum limit of {tradeBookCount.maxCount} trade books. 
            You can only add trades to existing trade books.
          </div>
        )}

        <div className="trade-book-selection">
          <div className="selection-options">
            <label>
              <input
                type="radio"
                name="tradeBookOption"
                checked={!isNewTradeBook}
                onChange={() => {
                  setIsNewTradeBook(false);
                  setNameError('');
                }}
                disabled={tradeBooks.length === 0}
              />
              <span>Select Existing Trade Book</span>
            </label>
            <label>
              <input
                type="radio"
                name="tradeBookOption"
                checked={isNewTradeBook}
                onChange={() => {
                  setIsNewTradeBook(true);
                  setNewTradeBookName(`TradeBook_${Date.now()}`);
                  setNameError('');
                }}
                disabled={tradeBookCount.count >= tradeBookCount.maxCount}
              />
              <span>Create New Trade Book</span>
            </label>
          </div>

          {!isNewTradeBook ? (
            <div className="form-group">
              <label htmlFor="existing-trade-book">Select Trade Book:</label>
              <select
                id="existing-trade-book"
                value={selectedTradeBookId}
                onChange={(e) => setSelectedTradeBookId(e.target.value)}
                className="trade-book-select"
                disabled={loading || tradeBooks.length === 0}
              >
                {tradeBooks.length === 0 ? (
                  <option value="">No trade books available</option>
                ) : (
                  tradeBooks.map((tb) => (
                    <option key={tb.id} value={tb.id}>
                      {tb.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="new-trade-book-name">New Trade Book Name:</label>
              <input
                id="new-trade-book-name"
                type="text"
                value={newTradeBookName}
                onChange={handleNameChange}
                className="trade-book-name-input"
                placeholder="Enter trade book name"
                disabled={loading}
                maxLength={100}
              />
              {nameError && <div className="error-message" style={{ marginTop: '5px', fontSize: '13px' }}>{nameError}</div>}
              <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                Only letters, numbers, and underscores allowed. Max 100 characters.
              </small>
            </div>
          )}
        </div>

        <BrokerSyncPanel
          brokers={brokers}
          disabled={loading || Boolean(importingBrokerId)}
          importingBrokerId={importingBrokerId}
          onConnect={connectBroker}
          onImport={importTodaysFills}
          message={brokerImportMessage}
        />

        <div className="trades-table-container">
          <div className="table-header">
            <h3>Enter Trades</h3>
            {loadingTrades && <span style={{ marginLeft: 10, color: '#666', fontSize: 14 }}>Loading trade book...</span>}
            <button type="button" className="btn btn-secondary" onClick={addMoreRows} disabled={loading}>
              Add More Rows
            </button>
          </div>
          
          <div className="table-wrapper">
            <table className="trades-table">
              <thead>
                <tr>
                  <th>Symbol *</th>
                  <th>Trade Type *</th>
                  <th>Quantity *</th>
                  <th>Entry Price *</th>
                  <th>Exit Price</th>
                  <th>Trade Date *</th>
                  <th>Segment</th>
                  <th>Exit/Expiry</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade, index) => {
                  const unsaved = trade && !trade.id && tradeRowPassesValidation(trade);
                  return (
                  <tr
                    key={trade.id || `new-${trade.brokerTradeId || index}-${index}`}
                    className={unsaved ? 'trades-table__row--unsaved' : undefined}
                  >
                    <td>
                      <input
                        type="text"
                        value={trade.symbol}
                        onChange={(e) => handleTradeChange(index, 'symbol', e.target.value)}
                        placeholder="e.g., NIFTY"
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <select
                        value={trade.tradeType}
                        onChange={(e) => handleTradeChange(index, 'tradeType', e.target.value)}
                        disabled={loading}
                      >
                        <option value="BUY">BUY</option>
                        <option value="SELL">SELL</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={trade.quantity}
                        onChange={(e) => handleTradeChange(index, 'quantity', e.target.value)}
                        placeholder="0"
                        min="0"
                        step="0.01"
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={trade.price}
                        onChange={(e) => handleTradeChange(index, 'price', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={trade.exitPrice}
                        onChange={(e) => handleTradeChange(index, 'exitPrice', e.target.value)}
                        placeholder="Optional"
                        min="0"
                        step="0.01"
                        disabled={loading}
                        title="Setting Exit Price creates 2 trades (entry + exit). Exit/Expiry date is mandatory when Exit Price is set."
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        value={trade.tradeDate}
                        onChange={(e) => handleTradeChange(index, 'tradeDate', e.target.value)}
                        disabled={loading}
                      />
                    </td>
                    <td>
                      <select
                        value={trade.segment}
                        onChange={(e) => handleTradeChange(index, 'segment', e.target.value)}
                        disabled={loading}
                      >
                        <option value="EQ">EQ</option>
                        <option value="FO">FO</option>
                        <option value="">--</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="date"
                        value={trade.expiry}
                        onChange={(e) => handleTradeChange(index, 'expiry', e.target.value)}
                        disabled={loading}
                        title={trade.exitPrice ? 'Required as exit date when Exit Price is set' : 'Expiry (FO) or exit date when Exit Price is set'}
                      />
                    </td>
                    <td>
                      {trade.id ? (
                        <button
                          type="button"
                          className="notes-button"
                          onClick={() => openNotesModal(trade)}
                          disabled={loading}
                          title="Journal & tags for this trade"
                        >
                          <span className="notes-button__icon" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                            </svg>
                          </span>
                          Notes
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="notes-button notes-button--disabled"
                          disabled
                          title="Save the trade book first to add notes"
                        >
                          Notes
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        <div className="submit-section">
          <button
            type="submit"
            className="btn btn-primary btn-large"
            onClick={handleSubmit}
            disabled={loading || (!isNewTradeBook && !selectedTradeBookId)}
          >
            {loading ? 'Submitting...' : 'Submit Trades'}
          </button>
        </div>
      </div>
      {notesModalOpen && notesModalTrade && (
        <div
          className="notes-modal-backdrop"
          onClick={closeNotesModal}
          role="presentation"
        >
          <div
            className="notes-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="notes-modal-title"
          >
            <header className="notes-modal__header">
              <div className="notes-modal__header-text">
                <h2 id="notes-modal-title" className="notes-modal__title">
                  Trade journal
                </h2>
                <p className="notes-modal__subtitle">
                  Tag context and capture what mattered for this fill.
                </p>
              </div>
              <button
                type="button"
                className="notes-modal__close"
                onClick={closeNotesModal}
                aria-label="Close"
                disabled={notesFetching || notesSaving}
              >
                <span aria-hidden="true">×</span>
              </button>
            </header>

            <div className="notes-modal__body">
              {notesFetching ? (
                <div className="notes-modal__loading" aria-live="polite">
                  <span className="notes-modal__spinner" aria-hidden="true" />
                  Loading notes…
                </div>
              ) : null}

              {notesSaveError ? (
                <div className="notes-modal__alert" role="alert">
                  {notesSaveError}
                </div>
              ) : null}

              <section
                className={`notes-modal__section${notesFetching ? ' notes-modal__section--dimmed' : ''}`}
                aria-label="Trade snapshot"
              >
                <h3 className="notes-modal__section-title">Snapshot</h3>
                <div className="notes-trade-snapshot">
                  <div className="notes-trade-snapshot__item notes-trade-snapshot__item--symbol">
                    <span className="notes-trade-snapshot__label">Symbol</span>
                    <span className="notes-trade-snapshot__value">{notesModalTrade.symbol || '—'}</span>
                  </div>
                  <div className="notes-trade-snapshot__item">
                    <span className="notes-trade-snapshot__label">Side</span>
                    <span
                      className={[
                        'notes-trade-snapshot__badge',
                        notesModalTrade.tradeType === 'BUY' && 'notes-trade-snapshot__badge--buy',
                        notesModalTrade.tradeType === 'SELL' && 'notes-trade-snapshot__badge--sell'
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {notesModalTrade.tradeType || '—'}
                    </span>
                  </div>
                  <div className="notes-trade-snapshot__item">
                    <span className="notes-trade-snapshot__label">Qty</span>
                    <span className="notes-trade-snapshot__value">{notesModalTrade.quantity ?? '—'}</span>
                  </div>
                  <div className="notes-trade-snapshot__item">
                    <span className="notes-trade-snapshot__label">Entry</span>
                    <span className="notes-trade-snapshot__value">{notesModalTrade.price ?? '—'}</span>
                  </div>
                  <div className="notes-trade-snapshot__item">
                    <span className="notes-trade-snapshot__label">Exit</span>
                    <span className="notes-trade-snapshot__value">{notesModalTrade.exitPrice ?? '—'}</span>
                  </div>
                  <div className="notes-trade-snapshot__item">
                    <span className="notes-trade-snapshot__label">Trade date</span>
                    <span className="notes-trade-snapshot__value">{notesModalTrade.tradeDate || '—'}</span>
                  </div>
                  <div className="notes-trade-snapshot__item">
                    <span className="notes-trade-snapshot__label">Expiry</span>
                    <span className="notes-trade-snapshot__value">{notesModalTrade.expiry || '—'}</span>
                  </div>
                </div>
              </section>

              {noteMeta && noteMeta.length > 0 ? (
                <section
                  className={`notes-modal__section${notesFetching ? ' notes-modal__section--dimmed' : ''}`}
                  aria-label="Structured tags"
                >
                  <h3 className="notes-modal__section-title">Context tags</h3>
                  <p className="notes-modal__section-hint">
                    Pick from suggestions or type your own — new values are saved for next time.
                  </p>
                  <div className="notes-modal__fields">
                    {noteMeta.map((type) => {
                      const current =
                        (notesForm.selections &&
                          notesForm.selections[type.key] &&
                          (notesForm.selections[type.key].label ||
                            notesForm.selections[type.key].value)) ||
                        '';
                      const inputId = `notes-${type.key}`;
                      const listId = `list-${type.key}`;
                      return (
                        <div key={type.id} className="notes-field">
                          <label className="notes-field__label" htmlFor={inputId}>
                            {type.label}
                          </label>
                          {type.description ? (
                            <p className="notes-field__description">{type.description}</p>
                          ) : null}
                          <div className="notes-field__input-wrap">
                            <input
                              id={inputId}
                              className="notes-field__input"
                              list={listId}
                              value={current}
                              onChange={(e) => {
                                const value = e.target.value;
                                setNotesForm((prev) => ({
                                  ...prev,
                                  selections: {
                                    ...prev.selections,
                                    [type.key]: { value, label: value }
                                  }
                                }));
                              }}
                              placeholder="Type or choose a suggestion…"
                              disabled={notesFetching || notesSaving}
                              autoComplete="off"
                            />
                          </div>
                          <datalist id={listId}>
                            {type.options &&
                              type.options.map((opt) => (
                                <option key={opt.id} value={opt.label}>
                                  {opt.label}
                                </option>
                              ))}
                          </datalist>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <section
                className={`notes-modal__section${notesFetching ? ' notes-modal__section--dimmed' : ''}`}
                aria-label="Free-form notes"
              >
                <h3 className="notes-modal__section-title">Reflection</h3>
                <p className="notes-modal__section-hint">
                  Optional — thesis, execution, emotions, or what you would do differently.
                </p>
                <div className="notes-field">
                  <textarea
                    className="notes-field__textarea"
                    rows={4}
                    value={notesForm.freeText}
                    onChange={(e) =>
                      setNotesForm((prev) => ({ ...prev, freeText: e.target.value }))
                    }
                    disabled={notesFetching || notesSaving}
                    placeholder="Write freely…"
                  />
                </div>
              </section>
            </div>

            <footer className="notes-modal__footer">
              <button
                type="button"
                className="notes-modal__btn notes-modal__btn--ghost"
                onClick={closeNotesModal}
                disabled={notesFetching || notesSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="notes-modal__btn notes-modal__btn--primary"
                onClick={async () => {
                  if (!notesModalTrade?.id) return;
                  setNotesSaveError('');
                  try {
                    setNotesSaving(true);
                    if (normalizeNotesForCompare(notesForm) === notesLoadedCompareRef.current) {
                      closeNotesModal();
                      return;
                    }
                    const payload = {
                      freeText: notesForm.freeText || '',
                      selections: Object.fromEntries(
                        Object.entries(notesForm.selections || {}).map(
                          ([key, val]) => [
                            key,
                            { value: val.value || val.label || '' }
                          ]
                        )
                      )
                    };
                    await axios.post(
                      `${API_BASE_URL}/api/trade-notes/${notesModalTrade.id}`,
                      payload
                    );
                    closeNotesModal();
                  } catch (e) {
                    console.error('Error saving trade notes:', e);
                    setNotesSaveError(
                      e.response?.data?.error || 'Could not save. Check your connection and try again.'
                    );
                  } finally {
                    setNotesSaving(false);
                  }
                }}
                disabled={notesFetching || notesSaving}
              >
                {notesSaving ? (
                  <>
                    <span className="notes-modal__btn-spinner" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  'Save journal'
                )}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManualTradeEntry;
